/**
 * Scenario Category Configuration
 *
 * Maps granular DB tags to broad consumer-facing categories.
 * Used for filter tabs and Netflix-style browse rows on the homepage.
 */
import { useTranslation } from 'react-i18next';

export interface ScenarioCategory {
  key: string;
  /** i18n key used to look up the translated label in scenarios.json */
  i18nKey: string;
  label: string;
  matchTags: string[];
  color: string;
  browseLabel: string;
}

export const SCENARIO_CATEGORIES: ScenarioCategory[] = [
  {
    key: "all",
    i18nKey: "all",
    label: "All",
    matchTags: [],
    color: "#1B1B1B",
    browseLabel: "All Scenarios",
  },
  {
    key: "emergency",
    i18nKey: "emergency",
    label: "Emergency",
    matchTags: ["emergency", "critical care", "trauma", "stroke"],
    color: "#B22234",
    browseLabel: "Emergency & Critical Care",
  },
  {
    key: "orthopedic",
    i18nKey: "orthopedic",
    label: "Orthopedic",
    matchTags: ["orthopedic", "joint replacement", "fracture", "hip fracture", "knee", "shoulder", "ankle", "hand", "sports medicine", "arthroscopic"],
    color: "#2563EB",
    browseLabel: "Orthopedic & Joint",
  },
  {
    key: "spine",
    i18nKey: "spineBack",
    label: "Spine & Back",
    matchTags: ["spine", "lumbar", "cervical", "fusion", "decompression", "pain management", "chronic pain", "disc replacement", "cage", "compression fracture", "neurosurgery"],
    color: "#7C3AED",
    browseLabel: "Spine & Back Pain",
  },
  {
    key: "surgery",
    i18nKey: "surgery",
    label: "Surgery",
    matchTags: ["gastrointestinal", "bariatric", "hernia", "surgery", "lithotripsy", "major surgery", "minor surgery", "minimally invasive"],
    color: "#0891B2",
    browseLabel: "General Surgery",
  },
  {
    key: "womens-health",
    i18nKey: "womensHealth",
    label: "Women's Health",
    matchTags: ["ob-gyn", "pregnancy"],
    color: "#DB2777",
    browseLabel: "Women's Health & Pregnancy",
  },
  {
    key: "cardiology",
    i18nKey: "cardiology",
    label: "Cardiology",
    matchTags: ["cardiology", "cardiac"],
    color: "#DC2626",
    browseLabel: "Heart & Cardiology",
  },
  {
    key: "cancer",
    i18nKey: "cancer",
    label: "Cancer",
    matchTags: ["oncology", "urology", "chemotherapy", "radiation", "immunotherapy"],
    color: "#9333EA",
    browseLabel: "Cancer & Oncology",
  },
  {
    key: "immune",
    i18nKey: "immuneInfectious",
    label: "Immune & Infectious",
    matchTags: ["immunology", "infectious-disease", "HIV", "rheumatology", "autoimmune", "allergy", "biologic"],
    color: "#059669",
    browseLabel: "Immune & Infectious Disease",
  },
  {
    key: "diabetes",
    i18nKey: "diabetesEndocrine",
    label: "Diabetes & Endocrine",
    matchTags: ["diabetes", "endocrinology", "thyroid"],
    color: "#D97706",
    browseLabel: "Diabetes & Endocrine",
  },
  {
    key: "pulmonology",
    i18nKey: "pulmonology",
    label: "Pulmonology",
    matchTags: ["pulmonology", "respiratory", "lung", "copd", "asthma", "sleep"],
    color: "#0D9488",
    browseLabel: "Lung & Respiratory",
  },
  {
    key: "transplant",
    i18nKey: "transplants",
    label: "Transplants",
    matchTags: ["transplant", "organ transplant", "donor", "graft"],
    color: "#4F46E5",
    browseLabel: "Organ Transplants",
  },
  {
    key: "gene-therapy",
    i18nKey: "geneTherapy",
    label: "Gene Therapy",
    matchTags: ["gene therapy", "genetic", "cell therapy", "car-t", "crispr"],
    color: "#BE185D",
    browseLabel: "Gene & Cell Therapies",
  },
];

/**
 * Check if a scenario matches a category based on its tags.
 * "all" category always matches. Otherwise, ANY tag overlap = match.
 */
export function scenarioMatchesCategory(
  tags: string[],
  category: ScenarioCategory
): boolean {
  if (category.key === "all") return true;
  if (!tags || tags.length === 0) return false;
  return tags.some((tag) => category.matchTags.includes(tag));
}

/**
 * Get category by key
 */
export function getCategoryByKey(key: string): ScenarioCategory | undefined {
  return SCENARIO_CATEGORIES.find((c) => c.key === key);
}

/**
 * Hook to get localized scenario categories.
 * Returns categories with labels and browseLabels translated via i18n.
 * Falls back to English if translations are not available.
 */
export function useLocalizedCategories(): ScenarioCategory[] {
  const { t } = useTranslation('scenarios');
  return SCENARIO_CATEGORIES.map(cat => ({
    ...cat,
    label: t(`categories.${cat.i18nKey}`, cat.label),
    browseLabel: t(`browseLabels.${cat.i18nKey}`, cat.browseLabel),
  }));
}
